import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getClub, getClubMemberships } from '../utils/clubs';
import { getUserProfile } from '../utils/database';
import { getAgeGroup, AGE_GROUP_ORDER } from '../utils/ageGroups';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import '../styles/Dashboard.css';

function ClubReports() {
  const { userId, clubId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [club, setClub] = useState(null);
  const [players, setPlayers] = useState([]);
  const [totalSignups, setTotalSignups] = useState(0);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || currentUser.uid !== userId) {
        navigate('/signin', { replace: true });
        return;
      }
      const userData = await getUserProfile(userId);
      setUser(userData);
    });
    return () => unsubscribe();
  }, [userId, navigate]);

  useEffect(() => {
    if (!clubId || !user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const clubData = await getClub(clubId);
        if (cancelled) return;
        const isOwner = clubData && (clubData.ownerId === userId || user.ownedClubIds?.includes(clubId));
        if (!clubData || !isOwner) {
          navigate(`/${userId}/dashboard`, { replace: true });
          return;
        }
        setClub(clubData);
      } catch (err) {
        console.error('[ClubReports] Club load error:', err);
        if (!cancelled) navigate(`/${userId}/dashboard`, { replace: true });
        return;
      } finally {
        setLoading(false);
      }

      // Non-critical data — don't redirect on failure
      try {
        const mems = await getClubMemberships(clubId);
        if (cancelled) return;
        setTotalSignups(mems.length);

        const profiles = await Promise.all(mems.map((m) => getUserProfile(m.userId)));
        if (cancelled) return;
        setPlayers(
          mems.map((m, i) => ({
            ...m,
            profile: profiles[i],
            ageGroup: getAgeGroup(profiles[i]?.birthdate),
          }))
        );
      } catch (err) {
        console.error('[ClubReports] Memberships load error (non-critical):', err);
      }

      try {
        const statsSnap = await getDocs(
          query(collection(db, 'clubs', clubId, 'monthlyStats'), orderBy('month'))
        );
        if (!cancelled) setMonthlyStats(statsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        if (!cancelled) setMonthlyStats([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clubId, userId, user, navigate]);

  const teams = club?.teams || [];
  const markupPerMonth = club?.markupPerMonth ?? 0;

  const signupsByAgeGroup = AGE_GROUP_ORDER.reduce((acc, ag) => {
    const count = players.filter((p) => p.ageGroup === ag).length;
    if (count > 0) acc.push({ ageGroup: ag, count });
    return acc;
  }, []);

  const signupsChartData = monthlyStats.length > 0
    ? monthlyStats.map((s) => ({ date: s.month, value: s.totalMembers ?? 0 }))
    : [{ date: 'Now', value: totalSignups }];

  const revenueChartData = monthlyStats.length > 0
    ? monthlyStats.map((s) => ({ date: s.month, value: (s.totalMembers ?? 0) * markupPerMonth }))
    : [{ date: 'Now', value: totalSignups * markupPerMonth }];

  const clubName = club?.name || '';
  const dataLoaded = !loading && club;

  return (
    <div className="dashboard-page has-bottom-nav">
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />
      <DashboardSidebar userId={userId} user={user} />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            {clubName ? (
              <h1 className="dashboard-content-loaded">{clubName}</h1>
            ) : (
              <div className="dashboard-skeleton-title" />
            )}
          </div>
        </div>

        <div className="club-dashboard-nav">
          <Link to={`/${userId}/club/${clubId}`} className="club-dashboard-nav-btn">
            Teams
          </Link>
          <Link to={`/${userId}/club/${clubId}/reports`} className="club-dashboard-nav-btn active">
            Reports
          </Link>
          <Link to={`/${userId}/club/${clubId}/manage`} className="club-dashboard-nav-btn club-dashboard-nav-right">
            Club Settings
          </Link>
        </div>

        <div className="settings-content">

          {/* Overview */}
          <div className="dashboard-card">
            {dataLoaded ? (
              <div className="dashboard-content-loaded">
                <div className="settings-form-item">
                  <label className="settings-form-label">Overview</label>
                  <div className="club-stat-row">
                    <span className="settings-description">Total signups</span>
                    <span className="settings-value">{totalSignups}</span>
                  </div>
                  <div className="club-stat-row">
                    <span className="settings-description">Teams</span>
                    <span className="settings-value">{teams.length}</span>
                  </div>
                </div>

                {signupsByAgeGroup.length > 0 && (
                  <div className="settings-form-item">
                    <label className="settings-form-label">Signups by Age Group</label>
                    {signupsByAgeGroup.map((item) => (
                      <div key={item.ageGroup} className="club-stat-row">
                        <span className="settings-description">{item.ageGroup}</span>
                        <span className="settings-value">{item.count}</span>
                      </div>
                    ))}
                  </div>
                )}

                {signupsByAgeGroup.length === 0 && (
                  <div className="settings-form-item">
                    <p className="club-empty-text">No signups yet. Share your landing page to get started.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="club-skeleton-card">
                <div className="club-skeleton-stat" />
                <div className="club-skeleton-row" />
                <div className="club-skeleton-row" />
              </div>
            )}
          </div>

          {/* Revenue */}
          <div className="dashboard-card">
            {dataLoaded ? (
              <div className="dashboard-content-loaded">
                <div className="settings-form-item">
                  <label className="settings-form-label">Revenue</label>
                  <div className="club-stat-row">
                    <span className="settings-description">Athletes enrolled</span>
                    <span className="settings-value">{totalSignups}</span>
                  </div>
                  <div className="club-stat-row">
                    <span className="settings-description">Athlete pays</span>
                    <span className="settings-value">${4 + markupPerMonth}/mo</span>
                  </div>
                  <div className="club-stat-row">
                    <span className="settings-description">Your club keeps</span>
                    <span className="settings-value">${markupPerMonth}/mo per athlete</span>
                  </div>
                  <div className="club-stat-row">
                    <span className="settings-description">Estimated monthly club revenue</span>
                    <span className="settings-value">${totalSignups * markupPerMonth}/mo</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="club-skeleton-card">
                <div className="club-skeleton-stat" />
                <div className="club-skeleton-row" />
                <div className="club-skeleton-row" />
                <div className="club-skeleton-row" />
                <div className="club-skeleton-row" />
              </div>
            )}
          </div>

          {/* Total Signups Chart */}
          <div className="dashboard-card">
            <div className="settings-form-item">
              <label className="settings-form-label">Total Signups</label>
            </div>
            {dataLoaded ? (
              <div className="dashboard-metric dashboard-content-loaded club-chart-loaded">
                <div className="dashboard-metric-chart">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={signupsChartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                      <defs>
                        <linearGradient id="clubPurpleGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="rgba(162, 150, 255, 0.40)" />
                          <stop offset="100%" stopColor="rgba(38, 39, 35, 0)" />
                        </linearGradient>
                        <filter id="clubPurpleShadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#A296FF" floodOpacity="0.4"/>
                        </filter>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#A296FF"
                        strokeWidth={2}
                        fill="url(#clubPurpleGradient)"
                        dot={{ fill: 'white', stroke: '#A296FF', strokeWidth: 1, r: 4 }}
                        style={{ filter: 'url(#clubPurpleShadow)' }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'rgba(13, 13, 13, 0.95)',
                          border: '1px solid rgba(162, 150, 255, 0.3)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '12px',
                          padding: '8px 12px'
                        }}
                        labelStyle={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}
                        formatter={(value) => [`${value}`, 'Athletes']}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.date || ''}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="dashboard-metric-info">
                  <div className="dashboard-metric-label">Athletes</div>
                  <div className="dashboard-metric-value">{totalSignups}</div>
                </div>
              </div>
            ) : (
              <div className="club-skeleton-card">
                <div className="club-skeleton-chart-block" />
              </div>
            )}
          </div>

          {/* Revenue by Month Chart */}
          {(dataLoaded ? markupPerMonth > 0 : true) && (
            <div className="dashboard-card">
              <div className="settings-form-item">
                <label className="settings-form-label">Revenue by Month</label>
              </div>
              {dataLoaded ? (
                markupPerMonth > 0 && (
                  <div className="dashboard-metric dashboard-content-loaded club-chart-loaded">
                    <div className="dashboard-metric-chart">
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={revenueChartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                          <defs>
                            <linearGradient id="clubRedGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="rgba(255, 0, 0, 0.40)" />
                              <stop offset="100%" stopColor="rgba(38, 39, 35, 0)" />
                            </linearGradient>
                            <filter id="clubRedShadow" x="-20%" y="-20%" width="140%" height="140%">
                              <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#FF0000" floodOpacity="0.4"/>
                            </filter>
                          </defs>
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#FF0000"
                            strokeWidth={2}
                            fill="url(#clubRedGradient)"
                            dot={{ fill: 'white', stroke: '#FF0000', strokeWidth: 1, r: 4 }}
                            style={{ filter: 'url(#clubRedShadow)' }}
                          />
                          <Tooltip
                            contentStyle={{
                              background: 'rgba(13, 13, 13, 0.95)',
                              border: '1px solid rgba(255, 0, 0, 0.3)',
                              borderRadius: '8px',
                              color: '#fff',
                              fontSize: '12px',
                              padding: '8px 12px'
                            }}
                            labelStyle={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}
                            formatter={(value) => [`$${value}`, 'Revenue']}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.date || ''}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="dashboard-metric-info">
                      <div className="dashboard-metric-label">Monthly Revenue</div>
                      <div className="dashboard-metric-value">${totalSignups * markupPerMonth}</div>
                    </div>
                  </div>
                )
              ) : (
                <div className="club-skeleton-card">
                  <div className="club-skeleton-chart-block" />
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      <BottomNav userId={userId} user={user} />
    </div>
  );
}

export default ClubReports;
